import {
  IExperienceTemplateResource,
  ISubtaskDataFieldChangeTemplate
} from '@dadohr/shared/domainTypes/experience';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import styled from '@emotion/styled';
import { Link, Typography, useTheme } from '@material-ui/core';
import { AlertCircle } from 'lucide-react';
import { SetStateAction } from 'react';
import { CustomInputLabel } from '../../../../../../../../components/CustomInputLabel';
import { FormRow } from '../../../../../../../../components/Form/FormRow';
import { InfoBox } from '../../../../../../../../components/InfoBox';
import {
  MarkdownEditorWithReplacementTokens,
  MarkdownTextWithReplacementPlaceholders,
  TokenDefinition
} from '../../../../../../../../components/MarkdownText';
import { useUnsafeTranslation } from '../../../../../../../../i18next';
import { useCurrentUser } from '../../../../../../../../services/currentUser';
import {
  useUserRuleConfigSimple,
  useUserRuleConfigWithAutocompletion
} from '../../../../../../../../services/user';
import { RuleBuilder, RuleRenderer } from '../../../../../RuleBuilder';
import { BlockSettingsSubPage, TaskBuilderContext } from '../../../types';
import {
  TaskBuilderBlockValidationState,
  TaskBuilderBlockValidationStateItem,
  useFieldValidator,
  VALIDATORS_DATA_FIELD_CHANGE
} from '../../../validation';

const RuleBuilderContainer = styled('div')`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const InfoHeader = styled('div')((p) => ({
  display: 'flex',
  fontWeight: p.theme.typography.fontWeightBold,
  fontSize: p.theme.typography.fontSize,
  marginBottom: p.theme.spacing()
}));

const InfoBody = styled('p')((p) => ({
  margin: 0,
  paddingLeft: p.theme.spacing(3)
}));

const ButtonLink = styled('div')(() => ({
  display: 'inline-block',
  cursor: 'pointer'
}));

export const BlockSettingsSubtaskDataFieldChange = ({
  value,
  validation,
  expTpl,
  setValidation,
  onChange,
  customFields,
  resources,
  goToSubPage,
  tokens
}: {
  value: ISubtaskDataFieldChangeTemplate;
  onChange: (nextValue: ISubtaskDataFieldChangeTemplate) => void;
  validation: TaskBuilderBlockValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<TaskBuilderBlockValidationState>
  >;
  expTpl: TaskBuilderContext;
  customFields: CustomUserField[];
  resources: IExperienceTemplateResource[];
  goToSubPage: (nextSubPage: BlockSettingsSubPage) => void;
  tokens: TokenDefinition[];
}) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();

  const invalidInstruction = validation?.fields.instructions || null;

  const validateInstructions = useFieldValidator(
    setValidation,
    VALIDATORS_DATA_FIELD_CHANGE,
    'instructions'
  );

  const config = useUserRuleConfigWithAutocompletion(
    expTpl.orgId,
    customFields,
    expTpl.tz,
    {
      orgId: expTpl.orgId,
      experienceTemplateId: expTpl.templateId
    }
  );

  return (
    <div>
      <FormRow>
        <div style={{ width: '100%' }}>
          <MarkdownEditorWithReplacementTokens
            value={value.data.instructions.t}
            onChange={(v) => {
              const nextValue: ISubtaskDataFieldChangeTemplate = Object.assign(
                {},
                value,
                {
                  data: Object.assign({}, value.data, {
                    instructions: Object.assign({}, value.data.instructions, {
                      t: v
                    })
                  })
                }
              );
              invalidInstruction && validateInstructions(nextValue, expTpl);
              onChange(nextValue);
            }}
            tokens={tokens}
            error={!!invalidInstruction}
            helperText={invalidInstruction}
            onBlur={() => validateInstructions(value, expTpl)}
            rows={12}
          />
        </div>
      </FormRow>
      <FormRow>
        <CustomInputLabel fullWidth label={t('Completion conditions')}>
          <RuleBuilderContainer>
            <RuleBuilder
              value={value.data.rules}
              onChange={(nextRule) => {
                if (nextRule) {
                  const nextValue: ISubtaskDataFieldChangeTemplate = {
                    ...value,
                    data: { ...value.data, rules: nextRule }
                  };
                  return onChange(nextValue);
                }
              }}
              config={config}
              variant="compact"
              nullable
              showEmptyRowWhenNull={false}
            />
          </RuleBuilderContainer>
        </CustomInputLabel>
      </FormRow>
      <FormRow>
        <InfoBox color="yellow">
          <InfoHeader>
            <AlertCircle
              size={20}
              style={{ marginRight: theme.spacing(0.6) }}
            />
            {t('Avoid daily reminders')}
          </InfoHeader>
          <InfoBody>
            {t(
              `When you use a Data Field Change subtask, we strongly recommend a reminder frequency of every 2-3 days or less. `
            )}
            <ButtonLink>
              <Link onClick={() => {}}>{t(`Why is this?`)}</Link>
            </ButtonLink>
          </InfoBody>
        </InfoBox>
      </FormRow>
    </div>
  );
};

export const BlockSettingsSubtaskDataFieldChangeReadOnly = ({
  value,
  expTpl,
  tokens
}: {
  value: ISubtaskDataFieldChangeTemplate;
  expTpl: TaskBuilderContext;
  tokens: TokenDefinition[];
}) => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const config = useUserRuleConfigSimple(org.customUserFields, org.tz, {
    orgId: expTpl.orgId,
    experienceTemplateId: expTpl.templateId
  });

  return (
    <>
      <FormRow>
        <Typography variant="h6" component="h6">
          {t('Instructions')}
        </Typography>
      </FormRow>
      <FormRow>
        <MarkdownTextWithReplacementPlaceholders
          t={value.data.instructions}
          throttleMs={500}
          tokens={tokens}
        />
      </FormRow>
      <FormRow>
        <Typography variant="h6" component="h6">
          {t('Completion conditions')}
        </Typography>
      </FormRow>
      <FormRow>
        <RuleRenderer
          value={value.data.rules}
          config={config}
          variant="multiline"
        />
      </FormRow>
    </>
  );
};
